import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Loader from 'components/_UI/Loader';
import ticketsApi from 'api/requests/tickets';
import { Column } from 'styled/alignment/Column';
import SalesPanel from '../../_sales/SalesPanel';
import RestrictedSalesPanel from 'modules/dashboard/events/collaboration/RestrictedSalesPanel';
import useEventAccess from 'hooks/useEventAccess';
import { EVENT_PERMISSIONS } from 'helpers/eventCollaboration';

const COPY = {
    // A develop rotulava este card como "Ingressos vendidos" também aqui,
    // por cópia do arquivo de ingressos.
    soldLabel: 'Produtos vendidos',
    iconName: 'label',
    sectionTitle: 'Desempenho por produto',
    sectionSubtitle: 'Quanto cada produto vendeu, arrecadou e ainda tem em estoque.',
    sortLabel: 'Ordenar produtos',
    hiddenLabel: 'Produto oculto',
    emptyText: 'Nenhum produto cadastrado para este evento.',
    chartTitle: 'Produtos vendidos por dia',
    chartSubtitle: 'Quantidade de produtos vendidos em cada dia, desde o início das vendas.',
    chartLabel: 'Produtos vendidos',
    chartEmptyText: 'Ainda não há vendas registradas para exibir no gráfico.',
};

/**
 * `salesRestricted` força o painel sem números de venda; sem ele vale o que o
 * convite libera no evento aberto no painel.
 */
export default function ProductDetail({ data, salesRestricted }) {
    const { can } = useEventAccess();
    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        getProducts(data);
    }, [data]);

    async function getProducts(slug) {
        setIsLoading(true);

        try {
            const { data: response } = await ticketsApi.getTicketForEvent(slug);
            setPayload(response);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    if (isLoading) {
        return (
            <ContentLoader>
                <Loader />
            </ContentLoader>
        );
    }

    // Colaborador sem `event.sales.view`: a API já nem manda os números.
    if (salesRestricted ?? !can(EVENT_PERMISSIONS.SALES_VIEW)) {
        return <RestrictedSalesPanel items={payload?.products || []} copy={COPY} />;
    }

    const soldOnline = Number(payload?.products_sold) || 0;
    const soldOffline = Number(payload?.products_sold_offline) || 0;
    const revenueOnline = Number(payload?.products_revenue_sold) || 0;
    const revenueOffline = Number(payload?.products_revenue_offline) || 0;

    /* Mesma leitura da aba de ingressos: `products_sold` e
       `products_revenue_sold` já são o recorte online, e o presencial vem nos
       campos `*_offline`. Os cards do topo ficam com o online, que fecha com a
       lista de desempenho; o PDV aparece em bloco próprio. */
    return (
        <SalesPanel
            items={payload?.products || []}
            copy={COPY}
            eventSlug={data}
            totals={{
                sold: soldOnline,
                total: payload?.products_total,
                revenueSold: revenueOnline,
                revenuePotential: payload?.products_revenue_total,
            }}
            channels={{ soldOnline, soldOffline, revenueOnline, revenueOffline }}
            soldByDay={payload?.products_sold_by_day || []}
        />
    );
}

const ContentLoader = styled(Column)`
    min-height: 320px;
    display: flex;
    justify-content: center;
    align-items: center;
`;
